import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FlaskConical, Mail, Plus, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface DialogFakeCalendarEventProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (emails: string[]) => void;
  isLoading?: boolean;
}

export default function DialogFakeCalendarEvent({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: DialogFakeCalendarEventProps) {
  const { t } = useTranslation();
  const [emails, setEmails] = useState<string[]>([
    "contact@lundimatin.fr",
    "contact@acelys.fr",
  ]);
  const [newEmail, setNewEmail] = useState("");

  const handleAddEmail = () => {
    const trimmedEmail = newEmail.trim().toLowerCase();
    if (
      trimmedEmail &&
      !emails.includes(trimmedEmail) &&
      isValidEmail(trimmedEmail)
    ) {
      setEmails([...emails, trimmedEmail]);
      setNewEmail("");
    }
  };

  const handleRemoveEmail = (index: number) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    onSubmit(emails);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex gap-2 items-center">
            <FlaskConical className="w-5 h-5" />
            {t("components.dialogFakeCalendarEvent.title")}
          </DialogTitle>
          <DialogDescription>
            {t("components.dialogFakeCalendarEvent.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Liste des emails */}
          <div className="space-y-2">
            <Label>
              {t("components.dialogFakeCalendarEvent.participantsEmails")}
            </Label>
            {emails.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("components.dialogFakeCalendarEvent.noParticipants")}
              </p>
            ) : (
              <div className="space-y-2">
                {emails.map((email, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center p-2 rounded-md bg-muted"
                  >
                    <div className="flex gap-2 items-center">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="font-mono text-sm">{email}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveEmail(index)}
                      className="p-0 w-6 h-6"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ajouter un nouveau participant */}
          <div className="space-y-2">
            <Label htmlFor="new-email">
              {t("components.dialogFakeCalendarEvent.addParticipant")}
            </Label>
            <div className="flex gap-2">
              <Input
                id="new-email"
                type="email"
                placeholder={t(
                  "components.dialogFakeCalendarEvent.emailPlaceholder"
                )}
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleAddEmail}
                disabled={
                  !newEmail.trim() ||
                  !isValidEmail(newEmail.trim()) ||
                  isLoading
                }
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {newEmail.trim() && !isValidEmail(newEmail.trim()) && (
              <p className="text-xs text-destructive">
                {t("components.dialogFakeCalendarEvent.invalidEmail")}
              </p>
            )}
          </div>

          {/* Info */}
          <div className="p-3 bg-blue-50 rounded-md border border-blue-200 dark:bg-blue-950 dark:border-blue-800">
            <p className="text-sm text-blue-900 dark:text-blue-100">
              {t("components.dialogFakeCalendarEvent.infoMessage")}
            </p>
            <p className="mt-1 text-xs text-blue-800 dark:text-blue-200">
              {t("components.dialogFakeCalendarEvent.infoSubMessage")}
            </p>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isLoading}>
              {t("components.dialogFakeCalendarEvent.cancel")}
            </Button>
          </DialogClose>
          <Button
            onClick={handleSubmit}
            disabled={emails.length === 0 || isLoading}
          >
            {isLoading
              ? t("components.dialogFakeCalendarEvent.creating")
              : t("components.dialogFakeCalendarEvent.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
